import type { AttackFlag, Loot } from '@mogzo/shared';
import { Badge } from '@mogzo/ui';
import { ChevronLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { Project } from '../lib/api';
import { timeAgo } from '../lib/format';
import { useApi } from '../lib/use-api';
import { Button, cardClass, ErrorNotice, fieldClass, Loading } from './ui';

type Env = 'test' | 'prod';

interface Person {
  playerId: string;
  /** Null in live, where players are anonymous ids. */
  name: string | null;
}
interface ListedAttack {
  id: string;
  mode: string;
  modeName: string;
  attacker: Person;
  defender: Person;
  status: 'open' | 'done' | 'expired';
  won: boolean | null;
  score: number | null;
  loot: Loot;
  flags: AttackFlag[];
  createdAt: string;
  reportedAt: string | null;
}
interface AttackList {
  summary: { bases: number; thisWeek: number; shielded: number; flaggedThisWeek: number };
  modes: { id: string; name: string }[];
  hasMore: boolean;
  attacks: ListedAttack[];
}
type AttackDetail = ListedAttack & { base: unknown; endsAt: string };

const who = (person: Person) => person.name ?? `Player ${person.playerId.slice(0, 10)}`;

/** "coins 40, gems 5", or a dash. */
export function lootText(loot: Loot) {
  const parts = Object.entries(loot).map(
    ([kind, amount]) => `${kind} ${amount.toLocaleString('en')}`,
  );
  return parts.length ? parts.join(', ') : '–';
}

/** A flag in a few words: "Loot cut from 9,999", "Done in 2 seconds". */
export function flagText(flag: AttackFlag) {
  if (flag.kind === 'loot_cut') return `${flag.loot} cut from ${flag.sent.toLocaleString('en')}`;
  if (flag.kind === 'too_fast') {
    return `Done in ${flag.seconds} ${flag.seconds === 1 ? 'second' : 'seconds'}`;
  }
  return `Hit the hourly limit ${flag.times} times`;
}

function Result({ attack }: { attack: Pick<ListedAttack, 'status' | 'won'> }) {
  if (attack.status === 'open') return <Badge tone="info">Attacking now</Badge>;
  if (attack.status === 'expired') return <Badge tone="neutral">Ran out of time</Badge>;
  return attack.won ? (
    <Badge tone="success">Attacker won</Badge>
  ) : (
    <Badge tone="neutral">Base held</Badge>
  );
}

function Flags({ flags }: { flags: AttackFlag[] }) {
  return (
    <span className="flex flex-wrap gap-1.5">
      {flags.map((flag) => (
        <Badge key={flag.kind} tone="danger">
          {flagText(flag)}
        </Badge>
      ))}
    </span>
  );
}

/** One attack: who, how it went, its flags, and the base as the attacker got it. */
function AttackPage({
  project,
  env,
  id,
  onBack,
}: {
  project: Project;
  env: Env;
  id: string;
  onBack: () => void;
}) {
  const attack = useApi<AttackDetail>(`/v1/dev/projects/${project.id}/attacks/${id}?env=${env}`);
  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="secondary" onClick={onBack}>
          <ChevronLeft size={16} aria-hidden="true" />
          Attacks
        </Button>
      </div>
      {attack.status === 'loading' && <Loading />}
      {attack.status === 'error' && <ErrorNotice error={attack.error} />}
      {attack.status === 'ready' && (
        <section
          aria-labelledby="attack-heading"
          className={`${cardClass} flex flex-col gap-4 p-5`}
        >
          <div className="flex flex-wrap items-center gap-3">
            <h2 id="attack-heading" className="text-lg font-semibold">
              {who(attack.data.attacker)} attacked {who(attack.data.defender)}
            </h2>
            <Result attack={attack.data} />
          </div>
          <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3 text-sm">
            {(
              [
                ['Mode', attack.data.modeName],
                ['Loot', lootText(attack.data.loot)],
                ['Score', attack.data.score === null ? '–' : String(attack.data.score)],
                ['Started', timeAgo(attack.data.createdAt)],
                ['Reported', attack.data.reportedAt ? timeAgo(attack.data.reportedAt) : 'Not yet'],
              ] as const
            ).map(([label, value]) => (
              <div key={label}>
                <dt className="text-[13px] text-muted">{label}</dt>
                <dd className="m-0 mt-0.5 font-semibold">{value}</dd>
              </div>
            ))}
          </dl>
          {attack.data.flags.length > 0 && <Flags flags={attack.data.flags} />}
          <div>
            <h3 className="mb-1.5 text-sm font-semibold">The base, as the attacker got it</h3>
            <pre className="max-h-[360px] overflow-auto rounded-[6px] bg-field px-3 py-2.5 font-mono text-xs leading-[19px]">
              {JSON.stringify(attack.data.base, null, 2)}
            </pre>
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * The data browser's Attacks page (M34): every attack on a base, newest first, with what
 * MOGZO flagged (loot over the mode's most, attacks faster than a person could play, players
 * who keep hitting the hourly limit).
 */
export function Attacks({ project, env }: { project: Project; env: Env }) {
  const [mode, setMode] = useState('');
  const [flagged, setFlagged] = useState(false);
  const [typed, setTyped] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(typed.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [typed]);

  const query = new URLSearchParams({ env, offset: String(offset) });
  if (mode) query.set('mode', mode);
  if (flagged) query.set('flagged', '1');
  if (search) query.set('search', search);
  const list = useApi<AttackList>(`/v1/dev/projects/${project.id}/attacks?${query}`);

  if (open)
    return <AttackPage project={project} env={env} id={open} onBack={() => setOpen(null)} />;

  const summary = list.status === 'ready' ? list.data.summary : null;
  const labelClass = 'flex flex-col gap-1 text-[13px] font-semibold text-muted';
  const cell = 'border-b border-divider px-3.5 py-3';
  return (
    <div className="flex flex-col gap-4">
      {summary && (
        <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
          {(
            [
              ['Bases saved', summary.bases],
              ['Attacks this week', summary.thisWeek],
              ['Shielded now', summary.shielded],
              ['Flagged this week', summary.flaggedThisWeek],
            ] as const
          ).map(([label, value]) => (
            <div key={label} className="mz-panel px-4 py-3">
              <dt className="text-[13px] text-muted">{label}</dt>
              <dd className="mz-text-title-lg m-0 mt-1 tabular-nums">
                {value.toLocaleString('en')}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold">Attacks</h2>
          <p className="mt-0.5 text-[13px] text-muted">
            Every attack on a base in {env === 'test' ? 'test' : 'live'}, newest first. Open one for
            the base as it was and what the attacker’s game sent.
          </p>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <label className={labelClass}>
            Mode
            <select
              value={mode}
              onChange={(event) => {
                setMode(event.target.value);
                setOffset(0);
              }}
              className={`${fieldClass} min-h-10`}
            >
              <option value="">All modes</option>
              {list.status === 'ready' &&
                list.data.modes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
            </select>
          </label>
          <label className={labelClass}>
            Show
            <select
              value={flagged ? 'flagged' : 'all'}
              onChange={(event) => {
                setFlagged(event.target.value === 'flagged');
                setOffset(0);
              }}
              className={`${fieldClass} min-h-10`}
            >
              <option value="all">All attacks</option>
              <option value="flagged">Flagged only</option>
            </select>
          </label>
          <label className={labelClass}>
            Player
            <input
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={env === 'test' ? 'Name or player id' : 'Player id'}
              className={`${fieldClass} min-h-10`}
            />
          </label>
        </div>
      </div>

      {list.status === 'loading' && <Loading />}
      {list.status === 'error' && <ErrorNotice error={list.error} />}
      {list.status === 'ready' &&
        (list.data.attacks.length === 0 ? (
          <p className={`${cardClass} px-5 py-6 text-sm text-muted`}>
            {search || mode || flagged
              ? 'No attacks like that.'
              : env === 'test'
                ? 'No attacks yet. They show here as soon as a tester attacks a base.'
                : 'No live attacks yet.'}
          </p>
        ) : (
          <div className={`${cardClass} overflow-x-auto`}>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="border-b border-divider px-3.5 py-2.5">Attacker</th>
                  <th className="border-b border-divider px-3.5 py-2.5">Defender</th>
                  <th className="border-b border-divider px-3.5 py-2.5">Result</th>
                  <th className="border-b border-divider px-3.5 py-2.5">Loot</th>
                  <th className="border-b border-divider px-3.5 py-2.5">When</th>
                  <th className="border-b border-divider px-3.5 py-2.5">Flags</th>
                  <th className="border-b border-divider px-3.5 py-2.5">
                    <span className="sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.data.attacks.map((a) => (
                  <tr key={a.id}>
                    <td className={`${cell} font-semibold`}>{who(a.attacker)}</td>
                    <td className={cell}>{who(a.defender)}</td>
                    <td className={cell}>
                      <Result attack={a} />
                    </td>
                    <td className={`${cell} tabular-nums`}>{lootText(a.loot)}</td>
                    <td className={`${cell} text-muted`}>{timeAgo(a.reportedAt ?? a.createdAt)}</td>
                    <td className={cell}>
                      <Flags flags={a.flags} />
                    </td>
                    <td className="border-b border-divider px-3.5 py-2 text-right">
                      <Button
                        variant="secondary"
                        onClick={() => setOpen(a.id)}
                        aria-label={`Open ${who(a.attacker)}’s attack on ${who(a.defender)}`}
                      >
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      {list.status === 'ready' && (offset > 0 || list.data.hasMore) && (
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - 50))}
          >
            Newer
          </Button>
          <Button
            variant="secondary"
            disabled={!list.data.hasMore}
            onClick={() => setOffset(offset + 50)}
          >
            Older
          </Button>
        </div>
      )}
      <p className="text-[13px] text-muted">
        <b className="text-ink">Flags</b> mark results your game should look at: loot over the
        mode’s most (cut to it), an attack finished faster than a person could play, or a player who
        keeps hitting the hourly limit.{' '}
        {env === 'test'
          ? 'In live, players show as anonymous ids and attacks can’t be changed.'
          : 'Players show as anonymous ids.'}
      </p>
    </div>
  );
}
